// Tiny Web Audio synth for Ludo SFX — no audio files needed.
// All sounds are synthesized so the client stays self-contained.

let ctx: AudioContext | null = null;
let master: GainNode | null = null;

function ensure(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = 0.45;
    master.connect(ctx.destination);
  }
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

// Browsers gate audio until the first user gesture — unlock on first tap/key.
if (typeof window !== 'undefined') {
  const unlock = () => ensure();
  window.addEventListener('pointerdown', unlock, { once: true });
  window.addEventListener('keydown', unlock, { once: true });
}

function tone({
  freq,
  freqEnd,
  type = 'sine',
  at = 0,
  dur,
  vol,
}: {
  freq: number;
  freqEnd?: number;
  type?: OscillatorType;
  at?: number;
  dur: number;
  vol: number;
}) {
  const c = ensure();
  if (!c || !master) return;
  const t0 = c.currentTime + at;
  const osc = c.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(Math.max(1, freq), t0);
  if (freqEnd) osc.frequency.exponentialRampToValueAtTime(Math.max(1, freqEnd), t0 + dur);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(vol, t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g);
  g.connect(master);
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);
}

function noise({ at = 0, dur, vol, highpass = 400 }: { at?: number; dur: number; vol: number; highpass?: number }) {
  const c = ensure();
  if (!c || !master) return;
  const t0 = c.currentTime + at;
  const len = Math.max(1, Math.floor(c.sampleRate * dur));
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buf;
  const filter = c.createBiquadFilter();
  filter.type = 'highpass';
  filter.frequency.value = highpass;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(vol, t0 + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(filter);
  filter.connect(g);
  g.connect(master);
  src.start(t0);
  src.stop(t0 + dur + 0.02);
}

/** Dice rattle — a rapid tumbling rattle that lands with a thunk. */
export function playDiceRoll() {
  const c = ensure();
  if (!c) return;
  for (let i = 0; i < 4; i++) {
    noise({ at: i * 0.055, dur: 0.045, vol: 0.22, highpass: 700 + i * 250 });
  }
  tone({ freq: 200, freqEnd: 110, type: 'triangle', at: 0.26, dur: 0.14, vol: 0.32 });
}

/** One tick as a token lands on the next box. Step lowers the pitch slightly. */
export function playHop(step = 0) {
  tone({ freq: 560 - Math.min(step, 5) * 34, freqEnd: 360, type: 'triangle', dur: 0.06, vol: 0.12 });
}

/** Cheerful arpeggio when a token reaches the centre. */
export function playHome() {
  [523.25, 659.25, 783.99, 1046.5].forEach((f, i) =>
    tone({ freq: f, at: i * 0.09, dur: 0.22, vol: 0.18, type: 'sine' })
  );
}

/** Descending "zag" when a token gets captured and is sent back to the yard. */
export function playCapture() {
  tone({ freq: 620, freqEnd: 130, type: 'triangle', dur: 0.24, vol: 0.3 });
  tone({ freq: 340, freqEnd: 210, type: 'triangle', at: 0.06, dur: 0.12, vol: 0.18 });
}